import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "api";
import { T_Api_Paginated, T_Api_Success_Res } from "./api.types";
import { CreditorDto } from "dtos/creditor.dto";
import { PaymentMethods } from "dtos/application.dto";

export const creditorService = {
  useGetPaginated: (params: {
    page: number;
    limit: number;
    sort?: { [key in keyof Partial<CreditorDto>]: 1 | -1 } | { _id: 1 | -1 };
    search?: string;
    inDraft: boolean;
  }) => {
    return useQuery({
      queryKey: ["creditors", params],
      queryFn: async () => {
        const res = await api.get(`/creditor`, { params });
        return res.data as T_Api_Success_Res<T_Api_Paginated<CreditorDto>>;
      },
    });
  },
  useGetPaginatedByAppKey: (params: {
    page: number;
    limit: number;

    search?: string;
    appKey?: string;
  }) => {
    return useQuery({
      queryKey: ["creditors", params],
      queryFn: async () => {
        const res = await api.get(`/creditor/app`, { params });
        return res.data as T_Api_Success_Res<T_Api_Paginated<CreditorDto>>;
      },
      enabled: !!params.appKey,
    });
  },

  useCreateCreditor: () => {
    return useMutation({
      mutationKey: ["creditor/create"],
      mutationFn: async ({ dto, bypass = true }: { dto: CreditorDto; bypass?: boolean }) => {
        const res = await api.post(`/creditor?bypass=${bypass}`, dto);
        return res.data as T_Api_Success_Res<CreditorDto>;
      },
    });
  },

  useUpdateCreditor: () => {
    return useMutation({
      mutationKey: ["creditor/update"],
      mutationFn: async ({ slug, dto }: { slug: string; dto: Partial<CreditorDto> }) => {
        const res = await api.patch(`/creditor/${slug}`, dto);
      },
    });
  },

  useLinkCreditor: () => {
    return useMutation({
      mutationKey: ["creditor/link"],
      mutationFn: async ({
        loan_id,
        creditor_id,
        partner_creditor_id,
      }: {
        loan_id: string;
        creditor_id: string;
        partner_creditor_id?: string;
      }) => {
        const res = await api.patch(`/creditor/link`, {
          loan_id,
          creditor_id,
          partner_creditor_id,
        });
      },
    });
  },

  useMergeCreditor: () => {
    return useMutation({
      mutationKey: ["creditor/merge"],
      mutationFn: async ({
        old_creditor_id,
        new_creditor_id,
      }: {
        old_creditor_id: string;
        new_creditor_id: string;
      }) => {
        const res = await api.post(`/creditor/merge`, {
          old_creditor_id,
          new_creditor_id,
        });
      },
    });
  },

  useGetAllCreditorNames: () => {
    return useQuery({
      queryKey: ["creditors/names/all"],
      queryFn: async () => {
        const res = await api.get(`/creditor/names`);
        return res.data as T_Api_Success_Res<{ name: string; _id: string; isPartner: boolean }[]>;
      },
    });
  },

  useParseCSV: () => {
    return useMutation({
      mutationKey: ["creditor/csv/sync"],
      mutationFn: async (formData: FormData) => {
        const res = await api.post("/creditor/csv/sync", formData);

        return res.data as T_Api_Success_Res<{ skippedRows: number[] }>;
      },
    });
  },

  useStartPaymentDetailsCapture: () => {
    return useMutation({
      mutationKey: ["creditor/collect/payment-info"],
      mutationFn: async ({ creditor_id, mode }: { creditor_id: string; mode: PaymentMethods }) => {
        const res = await api.post(`/creditor/collect/payment-info`, { creditor_id, mode });
        return res.data;
      },
    });
  },

  useGetPaymentMethodInfo: (creditor_id: string | undefined) => {
    return useQuery({
      queryKey: ["creditor", "creditor-payment/methods", creditor_id],
      queryFn: async () => {
        const res = await api.get(`/creditor/payment/info/${creditor_id}`);
        return res.data as T_Api_Success_Res<CreditorDto["payment_info"]>;
      },
      enabled: !!creditor_id,
    });
  },

  useEditPaymentMethodInfo: () => {
    return useMutation({
      mutationKey: ["creditor", "edit/creditor-payment-info"],
      mutationFn: async (body: { creditor_id: string; ids: string[] }) => {
        const res = await api.patch(`/creditor/edit-payment-info/${body.creditor_id}`, { ids: body.ids });
        return res.data;
      },
    });
  },

  useVerifyPaymentInfoPageToken: () => {
    return useMutation({
      mutationKey: ["creditor", "verify/payment-info-page"],
      mutationFn: async (body: { uuid: string; creditor_id: string; mode: PaymentMethods }) => {
        const res = await api.post(`/creditor/verify/payment-info-page`, body);

        return res.data as T_Api_Success_Res<{ found: boolean }>;
      },
    });
  },

  useRegisterPaymentMethods: () => {
    return useMutation({
      mutationKey: ["creditor", "save-creditor-payment-info"],
      mutationFn: async ({
        type,
        token,
        email,
        phone,
        first_name,
        last_name,
        creditor_id,
        mask,
        account_name,
        account_number,
        routing_number,
        bank_name,
        bank_type,
        address,
        city,
        state,
        zip,
        uuid,
        paynote_source_id,
        paynote_user_id,
      }: {
        email: string;
        creditor_id: string;
        type: PaymentMethods;
        token: string;
        phone: string;
        first_name: string;
        last_name: string;
        mask: string;
        account_number: string;
        routing_number: string;
        account_name: string;
        bank_name: string;
        bank_type: "savings" | "checking";
        uuid?: string;
        address: string;
        city: string;
        state: string;
        zip: string;
        paynote_source_id?: string;
        paynote_user_id?: string;
      }) => {
        await api.post(`/creditor/save-payment-info/${creditor_id}`, {
          // type: PaymentMethods.PAYNOTE,
          type,
          token,
          email,
          phone: String(phone),
          first_name,
          last_name,
          mask,
          account_name,
          account_number: String(account_number ?? ""),
          routing_number: String(routing_number ?? ""),
          uuid,
          bank_name,
          bank_type,
          address,
          city,
          state,
          zip,
          paynote_source_id,
          paynote_user_id,
        });
      },
    });
  },
};
